import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { clsx } from 'clsx';
import React, { ReactElement, useRef } from 'react';
import Select, { ActionMeta, components, SingleValue } from 'react-select';

import * as SearchStyles from './Search.module.scss';
import { SearchOption, SearchProps } from './Search.types';

import { tailwindConfig } from '@shared/utils';

const colors = tailwindConfig.theme?.colors as Record<
	string,
	Record<string, string>
>;

export const Search: React.FC<SearchProps> = ({
	onChange = () => {
		// this is intended
	},
	options,
	placeholder,
	noOptionsMessage = 'Geen resultaten gevonden',
	containerClassName,
	value,
	ariaLabel,
}): ReactElement => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const selectRef = useRef<any>();

	const ValueContainer: typeof components.ValueContainer = ({
		children,
		...props
	}) => {
		return (
			components.ValueContainer && (
				<components.ValueContainer {...props}>
					{children}
					<svg
						className={SearchStyles.searchIcon}
						width="17"
						height="17"
						viewBox="0 0 17 17"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							d="M16.0845 15.2001L12.1727 11.2891C13.3065 9.92798 13.8719 8.18211 13.7512 6.41472C13.6305 4.64733 12.8331 2.9945 11.5249 1.80006C10.2166 0.605618 8.49824 -0.038471 6.72719 0.00177892C4.95615 0.0420288 3.2688 0.763519 2.01616 2.01616C0.763519 3.2688 0.0420288 4.95615 0.00177892 6.72719C-0.038471 8.49824 0.605618 10.2166 1.80006 11.5249C2.9945 12.8331 4.64733 13.6305 6.41472 13.7512C8.18211 13.8719 9.92798 13.3065 11.2891 12.1727L15.2001 16.0845C15.2582 16.1425 15.3271 16.1886 15.403 16.22C15.4788 16.2514 15.5601 16.2676 15.6423 16.2676C15.7244 16.2676 15.8057 16.2514 15.8816 16.22C15.9574 16.1886 16.0264 16.1425 16.0845 16.0845C16.1425 16.0264 16.1886 15.9574 16.22 15.8816C16.2514 15.8057 16.2676 15.7244 16.2676 15.6423C16.2676 15.5601 16.2514 15.4788 16.22 15.403C16.1886 15.3271 16.1425 15.2582 16.0845 15.2001ZM1.26727 6.89227C1.26727 5.77975 1.59717 4.69221 2.21525 3.76719C2.83333 2.84216 3.71184 2.12119 4.73967 1.69545C5.76751 1.2697 6.89851 1.15831 7.98965 1.37535C9.08079 1.59239 10.0831 2.12812 10.8697 2.91479C11.6564 3.70146 12.1921 4.70374 12.4092 5.79489C12.6262 6.88603 12.5148 8.01703 12.0891 9.04486C11.6633 10.0727 10.9424 10.9512 10.0174 11.5693C9.09232 12.1874 8.00479 12.5173 6.89227 12.5173C5.40093 12.5156 3.97115 11.9225 2.91662 10.8679C1.86209 9.81338 1.26892 8.3836 1.26727 6.89227Z"
							fill={colors.blue.xdark}
						/>
					</svg>
				</components.ValueContainer>
			)
		);
	};

	const Option: typeof components.Option = ({ children, ...props }) => {
		return (
			components.Option && (
				<components.Option {...props}>
					{children}
					<ChevronRightIcon></ChevronRightIcon>
				</components.Option>
			)
		);
	};

	const ClearIndicator: typeof components.ClearIndicator = ({ ...props }) => {
		return (
			components.ClearIndicator && (
				<components.ClearIndicator {...props}>
					<CloseIcon></CloseIcon>
				</components.ClearIndicator>
			)
		);
	};

	const handleSelectChange = (
		option: SingleValue<SearchOption> | null,
		actionMeta: ActionMeta<SearchOption>,
	) => {
		/**
		 * Blur the select field after clearing the selected values
		 * There is currently no other option to achieve this
		 */
		if (actionMeta.action === 'clear' || actionMeta.action === 'remove-value') {
			setTimeout(() => {
				selectRef.current.blur();
				onChange(option);
			}, 1);
		} else {
			onChange(option);
		}
	};

	return (
		<div className={clsx(SearchStyles.searchContainer, containerClassName)}>
			<Select
				ref={selectRef}
				classNames={{
					control: () => SearchStyles.searchControl,
					input: () => SearchStyles.searchInput,
					placeholder: (state) => {
						return state.isFocused
							? SearchStyles.searchPlaceholderFocused
							: SearchStyles.searchPlaceholder;
					},
					option: (state) => {
						if (state.isSelected) {
							return clsx(
								SearchStyles.searchOption,
								SearchStyles.searchOptionSelected,
							);
						}

						if (state.isFocused) {
							return clsx(
								SearchStyles.searchOption,
								SearchStyles.searchOptionFocused,
							);
						}

						return SearchStyles.searchOption;
					},
					valueContainer: () => SearchStyles.searchValueContainer,
					singleValue: () => SearchStyles.searchSingleValue,
					noOptionsMessage: () => SearchStyles.searchNoOptionsMessage,
					clearIndicator: () => SearchStyles.searchCloseIndicator,
				}}
				components={{
					DropdownIndicator: () => null,
					IndicatorSeparator: () => null,
					ValueContainer,
					Option,
					ClearIndicator,
				}}
				blurInputOnSelect={true}
				backspaceRemovesValue={false}
				isClearable={true}
				isMulti={false}
				placeholder={placeholder}
				options={options}
				value={value}
				aria-label={ariaLabel}
				noOptionsMessage={() => noOptionsMessage}
				onChange={handleSelectChange}></Select>
		</div>
	);
};
